import axios from 'axios';
import {
  isValidToken,
  readBridgeRefreshToken,
  storeBridgeAccessToken,
  storeBridgeRefreshToken,
} from './bridgeTokenCookies';

/** Sign-out runs this through `resetBridgeTokenProvisioning`, under the commit lock. */
export { deleteUserBridgeTokens } from './bridgeTokenCookies';

export type BridgeTokenResponse = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type?: string;
  scope?: string;
};

export type BridgeTokenRequest = {
  /** USG origin without `/api`, e.g. `https://staging.services.unity.com`. */
  usgBaseUrl: string;
  /** Unity ID JWT (`unity_token`) from the session. */
  unityToken: string;
};

/** Replaces the axios rejection, which carries the bearer headers in its config. */
export class BridgeTokenRequestError extends Error {
  constructor(
    readonly status?: number,
    /** The response's `detail`: a 400 can be a dead refresh token or a server fault. */
    readonly detail?: string
  ) {
    super(`Bridge token: request failed${status ? ` (HTTP ${status})` : ''}.`);
    this.name = 'BridgeTokenRequestError';
  }
}

const COMMERCE_PLATFORM_PATH = '/api/commerce/platform';
const FORM_URLENCODED = 'application/x-www-form-urlencoded';
/**
 * Every provision and every interceptor renewal runs through one queue, so a
 * request left hanging by a blackholed proxy would stall all of them until the
 * OS gave up. Failing lets the queue drain and the error path run. Retries
 * share this budget rather than adding to it.
 */
const TOKEN_REQUEST_TIMEOUT_MS = 15_000;
const RETRY_DELAYS_MS = [300, 1_000];
// We always send a valid grant_type, so this reply is a server fault (unity-services-gateway #12563).
const UNSUPPORTED_GRANT_TYPE = /not supported grant type/i;

const parseUrl = (value: string): URL | undefined => {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
};

const isSecureOrigin = (url: URL) =>
  url.protocol === 'https:' ||
  (url.protocol === 'http:' &&
    (url.hostname === 'localhost' || url.hostname === '127.0.0.1'));

const isBareOrigin = (url: URL) =>
  url.pathname.replace(/\/+$/, '') === '' &&
  !url.search &&
  !url.hash &&
  !url.username &&
  !url.password;

/**
 * Anything but a bare absolute https origin builds a request URL the caller did
 * not mean: a relative value hits the marketplace's own origin, a
 * protocol-relative one hits whatever host follows the slashes, and the
 * server-side `USG_API` — the nearest value to hand — already ends in `/api`.
 */
export const resolveUsgBaseUrl = (usgBaseUrl: string): string => {
  const parsed = parseUrl(usgBaseUrl?.trim() ?? '');
  if (!parsed || !isSecureOrigin(parsed) || !isBareOrigin(parsed)) {
    throw new Error(
      'Bridge token: usgBaseUrl must be a bare absolute https origin with no path, query or credentials, e.g. https://staging.services.unity.com.'
    );
  }
  return parsed.origin;
};

const platformUrl = (usgBaseUrl: string, path: string) =>
  `${resolveUsgBaseUrl(usgBaseUrl)}${COMMERCE_PLATFORM_PATH}${path}`;

/** A 200 only satisfies the type at compile time; a missing field would reach a cookie as "undefined". */
const assertTokenResponse = (
  data: BridgeTokenResponse,
  expectRefreshToken = false
): BridgeTokenResponse => {
  if (
    !isValidToken(data?.access_token) ||
    !Number.isFinite(data?.expires_in) ||
    data.expires_in <= 0
  ) {
    throw new Error('Bridge token: response has no usable access token.');
  }
  if (expectRefreshToken && !isValidToken(data.refresh_token)) {
    throw new Error('Bridge token: response has no refresh token.');
  }
  return data;
};

const toRequestError = (error: unknown) => {
  const response = axios.isAxiosError(error) ? error.response : undefined;
  const detail = (response?.data as { detail?: unknown } | undefined)?.detail;
  return new BridgeTokenRequestError(
    response?.status,
    typeof detail === 'string' ? detail : undefined
  );
};

/** No response, a server fault, or a moment's limit; never a rejected credential. */
export const isTransientBridgeTokenError = ({
  status,
  detail,
}: BridgeTokenRequestError) =>
  status === undefined ||
  status >= 500 ||
  status === 408 ||
  status === 429 ||
  (status === 400 && UNSUPPORTED_GRANT_TYPE.test(detail ?? ''));

// Jittered so tabs that failed together do not retry together.
const withJitter = (delayMs: number) =>
  Math.round(delayMs * (0.8 + Math.random() * 0.4));

const sleep = (delayMs: number) =>
  new Promise((resolve) => setTimeout(resolve, delayMs));

const postToken = async (
  url: string,
  body: URLSearchParams | undefined,
  headers: Record<string, string>
): Promise<BridgeTokenResponse> => {
  const deadline = Date.now() + TOKEN_REQUEST_TIMEOUT_MS;
  for (let attempt = 0; ; attempt += 1) {
    try {
      const { data } = await axios.post<BridgeTokenResponse>(url, body, {
        headers,
        // axios reads 0 as no timeout at all.
        timeout: Math.max(deadline - Date.now(), 1),
      });
      return data;
    } catch (error) {
      const requestError = toRequestError(error);
      const delayMs =
        attempt < RETRY_DELAYS_MS.length
          ? withJitter(RETRY_DELAYS_MS[attempt])
          : undefined;
      if (
        delayMs === undefined ||
        !isTransientBridgeTokenError(requestError) ||
        Date.now() + delayMs >= deadline
      ) {
        throw requestError;
      }
      await sleep(delayMs);
      // A background tab's timer can overrun the budget it was checked against.
      if (Date.now() >= deadline) throw requestError;
    }
  }
};

/** Issues a user bridge token for a Unity ID token, and stores it in cookies. */
export const getBridgeToken = async ({
  usgBaseUrl,
  unityToken,
}: BridgeTokenRequest): Promise<BridgeTokenResponse> => {
  if (!isValidToken(unityToken)) {
    throw new Error('Bridge token: unityToken is blank.');
  }

  const data = assertTokenResponse(
    await postToken(
      platformUrl(usgBaseUrl, '/v2/user/token'),
      new URLSearchParams({ grant_type: 'client_credentials' }),
      {
        'Content-Type': FORM_URLENCODED,
        Authorization: `Bearer ${unityToken}`,
      }
    ),
    true
  );

  storeBridgeAccessToken(data.access_token, data.expires_in);
  storeBridgeRefreshToken(data.refresh_token);

  return data;
};

export const refreshUserBridgeToken = async ({
  usgBaseUrl,
}: Pick<BridgeTokenRequest, 'usgBaseUrl'>): Promise<BridgeTokenResponse> => {
  const refreshToken = readBridgeRefreshToken();
  if (!isValidToken(refreshToken)) {
    throw new Error('Bridge token: no user refresh token stored.');
  }

  const data = assertTokenResponse(
    await postToken(
      platformUrl(usgBaseUrl, '/v2/token'),
      new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }),
      { 'Content-Type': FORM_URLENCODED }
    )
  );

  storeBridgeAccessToken(data.access_token, data.expires_in);

  return data;
};
